import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MIcon } from '../../lib/micon';
import { KURZ_MINUTEN, abmeldenUndAufraeumen, erneuereToken, istKurzeSitzung, tokenDaten } from '../../lib/sitzung';
import '../ld/ld-fenster.css';

// ── Welle S8: kurze Sitzung (ohne „Angemeldet bleiben“) ────────────────────────────
// Benutzung (Tippen, Klicken, Tastatur, Scrollen) verlängert die Sitzung: das Token wird
// spätestens alle 5 Minuten erneuert, der Server lässt es nach 30 Minuten ohne Erneuerung
// verfallen. Nach 29 Minuten ohne Benutzung: „Du wirst gleich abgemeldet“ mit „Weiter arbeiten“;
// nach 30 Minuten: abmelden und aufräumen. Ungespeicherte Eingaben in einem offenen Fenster
// werden im Hinweis genannt. Bei „Angemeldet bleiben“ tut der Wächter nichts.
const MINUTE = 60 * 1000;
const WARNUNG_AB = (KURZ_MINUTEN - 1) * MINUTE;
const ERNEUERN_AB = 5 * MINUTE;

// Fenster, in denen getippt wurde (seit sie offen sind) — vorbelegte Felder zählen nicht.
// WeakSet: ein geschlossenes Fenster verschwindet aus dem DOM und damit auch von hier.
const bearbeitet = new WeakSet<Element>();
function merkeEingabe(e: Event) {
  const f = (e.target as Element | null)?.closest?.('.ld-ldsheet, .ld-z3-dialog');
  if (f && !f.closest('[data-sitzung-waechter]')) bearbeitet.add(f);
}
/** Offene Fenster mit Eingaben, die noch nicht gespeichert sind */
function ungespeicherteEingaben(): boolean {
  return Array.from(document.querySelectorAll('.ld-ldsheet, .ld-z3-dialog')).some(f => bearbeitet.has(f));
}

export function SitzungWaechter() {
  const [warnung, setWarnung] = useState<{ eingaben: boolean } | null>(null);
  const letzte = useRef(Date.now());
  const letzteErneuerung = useRef(0);
  const warnungRef = useRef(false);
  warnungRef.current = !!warnung;

  const erneuern = useCallback(async () => {
    letzteErneuerung.current = Date.now();
    const ok = await erneuereToken();
    if (!ok && istKurzeSitzung()) void abmeldenUndAufraeumen();
  }, []);

  useEffect(() => {
    if (!istKurzeSitzung()) return;
    const benutzt = () => {
      if (warnungRef.current) return;   // während des Hinweises zählt nur „Weiter arbeiten“
      letzte.current = Date.now();
      const iat = (tokenDaten()?.iat ?? 0) * 1000;
      if (Date.now() - iat >= ERNEUERN_AB && Date.now() - letzteErneuerung.current >= MINUTE) void erneuern();
    };
    const ereignisse = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;
    ereignisse.forEach(e => window.addEventListener(e, benutzt, { capture: true, passive: true }));
    window.addEventListener('input', merkeEingabe, true);
    const takt = window.setInterval(() => {
      const still = Date.now() - letzte.current;
      const exp = (tokenDaten()?.exp ?? 0) * 1000;
      if (still >= KURZ_MINUTEN * MINUTE || (exp && Date.now() >= exp)) { void abmeldenUndAufraeumen(); return; }
      if (still >= WARNUNG_AB && !warnungRef.current) setWarnung({ eingaben: ungespeicherteEingaben() });
    }, 5000);
    return () => { ereignisse.forEach(e => window.removeEventListener(e, benutzt, { capture: true })); window.removeEventListener('input', merkeEingabe, true); window.clearInterval(takt); };
  }, [erneuern]);

  if (!warnung) return null;
  const weiter = () => { letzte.current = Date.now(); setWarnung(null); void erneuern(); };
  return createPortal(
    <div data-sitzung-waechter="" onPointerDown={e => e.stopPropagation()} className="ld-modal-overlay"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3100, pointerEvents: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="ld-sitzung-titel" aria-describedby="ld-sitzung-text" className="ld-z3-dialog">
        <div className="ld-fe-kopf">
          <div className="ld-fe-kopf__text"><h3 id="ld-sitzung-titel" className="ld-fe-kopf__titel">Du wirst gleich abgemeldet</h3></div>
        </div>
        <div id="ld-sitzung-text" className="ld-z3-dialog__text">
          <p style={{ margin: 0 }}>Seit {KURZ_MINUTEN - 1} Minuten hast du LehrerDesk nicht benutzt. Zum Schutz der Daten wirst du in einer Minute abgemeldet.</p>
          {warnung.eingaben && (
            <p className="ld-sitzung-eingaben" style={{ margin: '10px 0 0', display: 'flex', gap: 8, color: 'var(--ld-warning-text)' }}>
              <MIcon name="warning" size={18} />Im offenen Fenster sind Eingaben noch nicht gespeichert. Sie gehen beim Abmelden verloren.
            </p>
          )}
        </div>
        <div className="ld-fe-fuss">
          <button type="button" className="ld-fe-knopf ld-fe-knopf--zweit" onClick={() => void abmeldenUndAufraeumen()}>Abmelden</button>
          <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" autoFocus onClick={weiter}>Weiter arbeiten</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
